import React, { useState, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface Props {
  sessionId: string;
  planName: string;
  onSuccess: (accessToken: string, userId: string, customerId: string) => void;
}

function evalStrength(pw: string) {
  const len  = pw.length >= 8;
  const up   = /[A-Z]/.test(pw);
  const num  = /[0-9]/.test(pw);
  const spec = /[^A-Za-z0-9]/.test(pw);
  let score = 0;
  if (len)  { score += 20; score += Math.min(36, (pw.length - 8) * 3); }
  if (up)   score += 15;
  if (num)  score += 15;
  if (spec) score += 14;
  return { valid: len && up && num && spec, score };
}

function strengthColor(s: number) {
  if (s < 30) return '#ef233c'; if (s < 55) return '#f77f00';
  if (s < 75) return '#f4d03f'; return '#2ecc71';
}

import API_BASE from '../../../config/api';
import axios from 'axios';

export default function StepAccount({ sessionId, planName, onSuccess }: Props) {
  const navigate = useNavigate();
  const [fullName, setFullName]   = useState('');
  const [company, setCompany]     = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [agreed, setAgreed]       = useState(false);
  const [showPwd, setShowPwd]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const strength = useMemo(() => evalStrength(password), [password]);
  const matches  = confirm.length > 0 && password === confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Validate inline so the user always gets feedback on click
    if (!fullName.trim())   { setError('Please enter your full name.'); return; }
    if (!company.trim())    { setError('Please enter your company name.'); return; }
    if (!email.trim())      { setError('Please enter your work email.'); return; }
    if (strength.score < 50){ setError('Password is too weak — add uppercase, numbers, or special characters.'); return; }
    if (!matches)           { setError('Passwords do not match.'); return; }
    if (!agreed)            { setError('Please accept the Terms of Service and Privacy Policy.'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/account`, {
        email, password, full_name: fullName.trim(), company_name: company.trim(),
      });
      onSuccess(data.access_token, data.user_id, data.customer_id);
    } catch (err: any) {
      console.error('[StepAccount] submit error:', err?.response?.status, err?.response?.data ?? err?.message);
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((v: any) => v.msg ?? JSON.stringify(v)).join('; ')
        : typeof d === 'string' ? d : 'Could not create account — please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box maxWidth={480} mx="auto" sx={{ position: 'relative' }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary' }}
        aria-label="Go back"
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
        Create your Shield account
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Setting up your <strong>{planName}</strong> plan — takes about 30 seconds
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField label="Full name" fullWidth required value={fullName}
            onChange={(e) => setFullName(e.target.value)} autoFocus />
          <TextField label="Company name" fullWidth required value={company}
            onChange={(e) => setCompany(e.target.value)} />
          <TextField label="Work email" type="email" fullWidth required value={email}
            onChange={(e) => setEmail(e.target.value)} />

          <TextField
            label="Password"
            type={showPwd ? 'text' : 'password'}
            fullWidth required value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwd((p) => !p)} edge="end" size="small">
                    {showPwd ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {password.length > 0 && (
            <Box>
              <LinearProgress variant="determinate" value={strength.score}
                sx={{ height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': { bgcolor: strengthColor(strength.score), borderRadius: 3 } }} />
              <Typography variant="caption" sx={{ color: strengthColor(strength.score) }}>
                {strength.score < 30 ? 'Weak' : strength.score < 55 ? 'Fair' : strength.score < 75 ? 'Good' : 'Strong'} password
              </Typography>
              {strength.score < 50 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {[
                    password.length < 8 && '8+ characters',
                    !/[A-Z]/.test(password) && 'uppercase letter',
                    !/[0-9]/.test(password) && 'number',
                    !/[^A-Za-z0-9]/.test(password) && 'special character',
                  ].filter(Boolean).join(', ')} required
                </Typography>
              )}
            </Box>
          )}

          <TextField
            label="Confirm password"
            type="password"
            fullWidth required value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={confirm.length > 0 && !matches}
            helperText={confirm.length > 0 && !matches ? "Passwords don't match" : ''}
          />

          <FormControlLabel
            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} size="small" />}
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link href="/terms" target="_blank" rel="noopener noreferrer" color="primary">Terms of Service</Link> and{' '}
                <Link href="/privacy" target="_blank" rel="noopener noreferrer" color="primary">Privacy Policy</Link>
              </Typography>
            }
          />

          <Button type="submit" variant="contained" size="large" disabled={loading}
            sx={{ fontWeight: 700, py: 1.5 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Create My Account →'}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
        Already have an account?{' '}
        <Link component={RouterLink} to="/login" color="primary">Sign in</Link>
      </Typography>
    </Box>
  );
}
